import QRCode from 'qrcode';
import { APIHOST } from '../config';

/** Horizontal gap between `.body-card` right and promo card left (see CSS `right`). */
const MAIN_COLUMN_GAP_PX = 10;

/** If column is missing, strip inline `right` so CSS default applies. */
const DEFAULT_RIGHT_FROM_VIEWPORT_PX = 10;

/** Reject bogus `getBoundingClientRect()` (desktop column is wider when promo shows). */
const MIN_EXPECTED_BODY_CARD_WIDTH_PX = 32;

const DEFAULT_COOLDOWN_SECONDS = 60;
const RACK_ATTACK_ERROR_CODE = 13429;

function getI18n() {
  return window.jobPageAppI18n || {};
}

function digitsOnly(value) {
  return String(value || '').replace(/\D/g, '');
}

/** @param {string} tenDigits */
function isNanpNational(tenDigits) {
  return tenDigits.length === 10;
}

function formatCooldown(template, seconds) {
  if (!template) return `Resend in ${seconds}s`;
  return template.replace(/\{\{\s*seconds\s*\}\}/g, String(seconds));
}

function formatRateLimited(template, seconds) {
  if (!template) return `Please wait ${seconds} seconds before requesting another link.`;
  return template.replace(/\{\{\s*seconds\s*\}\}/g, String(seconds));
}

/**
 * NANP formatted display (+1 assumed); input stores national digits only visually as (AAA) EEE-GGGG
 * @param {string} digits
 */
function formatNanpDisplay(digits) {
  const d = digitsOnly(digits).slice(0, 10);
  if (d.length === 0) return '';
  if (d.length <= 3) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function normalizeConfig(config) {
  if (!config) return null;
  if (typeof config === 'string') return { onelink: config };
  return config;
}

function buildSmsUrl(digestKey, scope) {
  const path = `/v1/careers/positions/${encodeURIComponent(digestKey)}/worker_hub_app_download_sms`;
  const base = `${APIHOST}${path}`;
  const params = new URLSearchParams();
  Object.entries(scope || {}).forEach(([key, value]) => {
    if (value != null && value !== '') {
      params.append(key, String(value));
    }
  });
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

function parseRetryAfterHeader(headers) {
  const raw = headers && (headers['retry-after'] || headers['Retry-After']);
  if (raw == null || raw === '') return null;
  const seconds = parseInt(String(raw), 10);
  return Number.isFinite(seconds) && seconds > 0 ? seconds : null;
}

function getRetrySeconds(data, headers) {
  if (data && Number.isFinite(data.retry_after_seconds) && data.retry_after_seconds > 0) {
    return data.retry_after_seconds;
  }
  return parseRetryAfterHeader(headers) || DEFAULT_COOLDOWN_SECONDS;
}

/**
 * @param {object} data
 * @param {Record<string, string>} i18n
 * @returns {{
 *   tone: 'success' | 'error',
 *   message: string,
 *   cooldownSeconds?: number,
 *   disableSend?: boolean,
 * }}
 */
function parseSmsPayload(data, i18n, headers) {
  if (!data || typeof data !== 'object') {
    return { tone: 'error', message: i18n.sms_error || '' };
  }

  if (data.error_code === RACK_ATTACK_ERROR_CODE) {
    const retrySeconds = getRetrySeconds(data, headers);
    return {
      tone: 'error',
      message: i18n.sms_too_many_requests || data.message || '',
      cooldownSeconds: retrySeconds,
    };
  }

  if (data.sent === true) {
    return {
      tone: 'success',
      message: i18n.sms_sent || '',
      cooldownSeconds: DEFAULT_COOLDOWN_SECONDS,
    };
  }

  const { reason } = data;
  if (reason === 'link_unavailable') {
    return {
      tone: 'error',
      message: i18n.sms_link_unavailable || '',
      disableSend: true,
    };
  }
  if (reason === 'suppressed') {
    return { tone: 'error', message: i18n.sms_suppressed || '' };
  }
  if (reason === 'send_failed') {
    return { tone: 'error', message: i18n.sms_send_failed || '' };
  }
  if (reason === 'invalid_phone') {
    return { tone: 'error', message: i18n.invalid_phone || '' };
  }
  if (reason === 'rate_limited') {
    const retrySeconds = getRetrySeconds(data, headers);
    return {
      tone: 'error',
      message: formatRateLimited(i18n.sms_rate_limited, retrySeconds),
      cooldownSeconds: retrySeconds,
    };
  }

  return { tone: 'error', message: i18n.sms_error || '' };
}

/**
 * @param {import('axios').AxiosResponse} response
 * @param {Record<string, string>} i18n
 */
function parseSmsResponse(response, i18n) {
  return parseSmsPayload(response.data, i18n, response.headers);
}

/**
 * @param {import('axios').AxiosError} error
 * @param {Record<string, string>} i18n
 */
function parseSmsError(error, i18n) {
  const response = error && error.response;
  if (!response) {
    return { tone: 'error', message: i18n.sms_error || '' };
  }

  const { status, data, headers } = response;

  if (status === 422) {
    return parseSmsPayload(
      { sent: false, reason: 'invalid_phone', ...(data || {}) },
      i18n,
      headers,
    );
  }

  if (status === 429) {
    return parseSmsPayload(data || {}, i18n, headers);
  }

  if (status === 400 || status === 404) {
    return { tone: 'error', message: i18n.sms_error || '' };
  }

  if (status === 200) {
    return parseSmsPayload(data, i18n, headers);
  }

  return parseSmsPayload(data, i18n, headers);
}

function initDesktopAppCard(configInput) {
  const config = normalizeConfig(configInput);
  if (!config || !config.onelink) return;

  const { onelink, digestKey, scope: scopeInput = {} } = config;
  const scope = scopeInput;

  const cardRoot = document.getElementById('job-page-app-desktop-card');
  if (!cardRoot) return;
  if (window.jobPageDesktopAppCardInited) return;
  window.jobPageDesktopAppCardInited = true;

  const qrImg = cardRoot.querySelector('.job-page-app-desktop-card__qr');
  const phoneInput = cardRoot.querySelector('.js-job-page-app-phone');
  const sendBtn = cardRoot.querySelector('.js-job-page-app-send-sms');
  const feedback = cardRoot.querySelector('.js-job-page-app-send-sms-feedback');
  const dismissBtn = cardRoot.querySelector('.js-job-page-app-desktop-card-dismiss');
  const expandChip = cardRoot.querySelector('.js-job-page-app-desktop-card-expand');
  const cardWrap = cardRoot.querySelector('.job-page-app-desktop-card__wrap');
  const i18n = getI18n();
  const sendLabel = sendBtn && sendBtn.getAttribute('data-send-label')
    ? sendBtn.getAttribute('data-send-label')
    : 'Send';

  let cooldownTimer = null;
  let smsRequestInFlight = false;

  function setSmsFeedback(message, tone) {
    if (!feedback) return;
    feedback.textContent = message || '';
    feedback.classList.remove(
      'job-page-app-desktop-card__sms-feedback--error',
      'job-page-app-desktop-card__sms-feedback--success',
    );
    if (tone === 'error') {
      feedback.classList.add('job-page-app-desktop-card__sms-feedback--error');
    } else if (tone === 'success') {
      feedback.classList.add('job-page-app-desktop-card__sms-feedback--success');
    }
  }

  function clearCooldown() {
    if (cooldownTimer) {
      clearInterval(cooldownTimer);
      cooldownTimer = null;
    }
  }

  function startCooldown(seconds) {
    if (!sendBtn) return;
    clearCooldown();
    let remainingSec = Math.max(1, Math.floor(seconds));
    sendBtn.disabled = true;
    const tpl = i18n.sms_cooldown || '';

    function refreshCooldownLabel() {
      sendBtn.textContent = formatCooldown(tpl, remainingSec);
    }

    refreshCooldownLabel();
    cooldownTimer = setInterval(() => {
      remainingSec -= 1;
      if (remainingSec <= 0) {
        clearCooldown();
        sendBtn.disabled = false;
        sendBtn.textContent = sendLabel;
        return;
      }
      refreshCooldownLabel();
    }, 1000);
  }

  function setSendDisabled(disabled) {
    if (sendBtn) sendBtn.disabled = disabled;
    if (phoneInput && disabled) phoneInput.setAttribute('aria-disabled', 'true');
    if (phoneInput && !disabled) phoneInput.removeAttribute('aria-disabled');
  }

  function applySmsResult(result) {
    setSmsFeedback(result.message, result.tone);

    if (result.disableSend) {
      setSendDisabled(true);
      return;
    }

    if (result.cooldownSeconds) {
      startCooldown(result.cooldownSeconds);
      return;
    }

    setSendDisabled(false);
  }

  function setExpandedUi(isExpanded) {
    cardRoot.classList.toggle('is-collapsed', !isExpanded);
    if (expandChip) {
      expandChip.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
    }
    if (cardWrap) {
      cardWrap.setAttribute('aria-hidden', isExpanded ? 'false' : 'true');
    }
  }

  /** Width of the visible promo UI (chip when collapsed, full card when expanded). */
  function measurePromoWidth() {
    if (cardRoot.classList.contains('is-collapsed') && expandChip) {
      return Math.max(expandChip.getBoundingClientRect().width, 1);
    }
    if (cardWrap) {
      return Math.max(cardWrap.getBoundingClientRect().width, 1);
    }
    return Math.max(cardRoot.getBoundingClientRect().width, 1);
  }

  function syncCardRightFromBodyCard() {
    if (!cardRoot) return;

    cardRoot.style.left = 'auto';
    cardRoot.style.top = 'auto';

    const anchor = document.querySelector(
      '.body-wrapper .body-card.corporate-body-card',
    );
    const cardWidth = measurePromoWidth();

    const anchorRect = anchor ? anchor.getBoundingClientRect() : null;
    const anchorRectLooksValid = Boolean(
      anchorRect
        && anchorRect.width >= MIN_EXPECTED_BODY_CARD_WIDTH_PX
        && anchorRect.right > anchorRect.left
        && anchorRect.bottom >= anchorRect.top,
    );

    let columnRight;
    if (anchorRectLooksValid && anchorRect) {
      columnRight = anchorRect.right;
    }

    if (columnRight == null || !Number.isFinite(columnRight)) {
      cardRoot.style.removeProperty('right');
      return;
    }

    let rightPx = columnRight - cardWidth - anchorRect.width - MAIN_COLUMN_GAP_PX;
    rightPx = Math.max(DEFAULT_RIGHT_FROM_VIEWPORT_PX, rightPx);
    cardRoot.style.right = `${rightPx}px`;
  }

  function scheduleSyncRightOnce() {
    requestAnimationFrame(() => {
      syncCardRightFromBodyCard();
    });
  }

  function collapseCard() {
    setExpandedUi(false);
    scheduleSyncRightOnce();
  }

  function expandCard() {
    setExpandedUi(true);
    scheduleSyncRightOnce();
  }

  if (dismissBtn) {
    dismissBtn.addEventListener('click', collapseCard);
  }

  if (expandChip) {
    expandChip.addEventListener('click', expandCard);
  }

  window.addEventListener('resize', syncCardRightFromBodyCard, { passive: true });

  if (typeof ResizeObserver !== 'undefined') {
    const layoutObserver = new ResizeObserver(scheduleSyncRightOnce);
    layoutObserver.observe(cardRoot);
    if (cardWrap) layoutObserver.observe(cardWrap);
    if (expandChip) layoutObserver.observe(expandChip);
  }

  setExpandedUi(true);
  syncCardRightFromBodyCard();
  scheduleSyncRightOnce();

  if (phoneInput) {
    const syncPhoneFormatting = () => {
      const raw = digitsOnly(phoneInput.value).slice(0, 10);
      phoneInput.value = formatNanpDisplay(raw);
    };
    phoneInput.addEventListener('input', syncPhoneFormatting);
    phoneInput.addEventListener('blur', syncPhoneFormatting);
  }

  function ensureQrFromOnelink() {
    if (!qrImg || !onelink) return;

    QRCode.toDataURL(String(onelink), {
      width: 160,
    })
      .then((dataUrl) => {
        qrImg.src = dataUrl;
      })
      .catch(() => {
        qrImg.removeAttribute('src');
        qrImg.alt = '';
      });
  }

  ensureQrFromOnelink();

  if (sendBtn) {
    sendBtn.addEventListener('click', () => {
      setSmsFeedback('', null);
      if (sendBtn.disabled || smsRequestInFlight) return;

      const national = digitsOnly(phoneInput && phoneInput.value);
      if (!isNanpNational(national)) {
        setSmsFeedback(i18n.invalid_phone || '', 'error');
        return;
      }

      if (!digestKey) {
        setSmsFeedback(i18n.sms_error || '', 'error');
        return;
      }

      const url = buildSmsUrl(digestKey, scope);
      const phone = `+1${national}`;

      smsRequestInFlight = true;
      setSendDisabled(true);

      axios.post(url, { phone })
        .then((response) => {
          applySmsResult(parseSmsResponse(response, i18n));
        })
        .catch((error) => {
          applySmsResult(parseSmsError(error, i18n));
        })
        .finally(() => {
          smsRequestInFlight = false;
        });
    });
  }
}

export default initDesktopAppCard;
